<!--

  @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
  @license AGPL-3.0

  This code is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as published by
  the Free Software Foundation, either version 3 of the License, or
  (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.

  Any use of the work other than as authorized under this license or copyright law is prohibited.

-->

<template>
  <svg
    :class="className"
    width="16" height="16"
    aria-hidden="true"
    v-bind="attributes"
    v-html="data"
    ref="svg"
  ></svg>
</template>

<script setup>
import { ref, nextTick } from 'vue'
import { settings as coreSettings } from '@/io.ox/core/settings'
import { iconCache } from '@/io.ox/vueComponents/base-icon-shared.js'
import questionOctagonIcon from 'bootstrap-icons/icons/question-octagon.svg?raw'

const props = defineProps({
  iconName: { type: String, required: true }
})

const emit = defineEmits(['load'])

const svg = ref(null)
const data = ref('')
const className = ref('')
const attributes = ref({})

const customIcons = coreSettings.get('theming/customIcons', {})
const path = /^(\/|\.\/)/.test(props.iconName)
  // use iconName as-is if it starts with / or ./ (exemplary use-case: load AI icon provided by a plugin)
  ? props.iconName
  : (customIcons[props.iconName] || `./themes/default/icons/${props.iconName}`)

if (iconCache[path]) update(iconCache[path])

fetch(path)
  .then(async (res) => {
    if (!res.ok) throw new Error(`Could not load icon ${path}`)
    const text = await res.text()
    const parser = new DOMParser()
    const icon = parser.parseFromString(text, 'image/svg+xml').getElementsByTagName('svg')[0]
    iconCache[path] = icon
    await update(icon)
  })
  .catch(() => {
    data.value = questionOctagonIcon
  })

async function update (icon) {
  for (const { name, value } of (icon.attributes || [])) {
    if (name === 'class') className.value = value
    else attributes.value[name] = value
  }
  data.value = icon.innerHTML
  await nextTick()
  emit('load', svg.value)
}

</script>
